import * as THREE from "three";

import type {
  LoomAuthoringDocument,
  LoomAirfoil,
  LoomAxis,
  LoomPrimitiveDefinition,
  LoomProfile2d,
  LoomSceneNode,
} from "./loomAuthoringModel";
import { loomMirrorAeroGate } from "./loomAuthoringModel";
import { addLoomSelectionOutlines } from "./loomSelectionOutline";

function profilePoints(profile: LoomProfile2d, count = 40): THREE.Vector2[] {
  if (profile.kind === "polygon") {
    return profile.verticesM.map(([x, y]) => new THREE.Vector2(x, y));
  }
  const points: THREE.Vector2[] = [];
  for (let index = 0; index < count; index += 1) {
    const angle = (index / count) * Math.PI * 2;
    if (profile.kind === "circle") {
      points.push(new THREE.Vector2(Math.cos(angle) * profile.radiusM, Math.sin(angle) * profile.radiusM));
    } else if (profile.kind === "ellipse") {
      points.push(new THREE.Vector2(Math.cos(angle) * profile.semiAxesM[0], Math.sin(angle) * profile.semiAxesM[1]));
    } else if (profile.kind === "rounded-box") {
      const [halfX, halfY] = profile.halfExtentsM;
      const scale = Math.max(Math.abs(Math.cos(angle)) / halfX, Math.abs(Math.sin(angle)) / halfY);
      points.push(new THREE.Vector2(Math.cos(angle) / scale, Math.sin(angle) / scale));
    } else {
      const [semiX, semiY] = profile.semiAxesM;
      const cosine = Math.cos(angle);
      const sine = Math.sin(angle);
      points.push(new THREE.Vector2(
        semiX * Math.sign(cosine) * Math.abs(cosine) ** (2 / profile.widthExponent),
        semiY * Math.sign(sine) * Math.abs(sine) ** (2 / profile.heightExponent),
      ));
    }
  }
  return points;
}

function binomial(order: number, index: number): number {
  let result = 1;
  for (let step = 1; step <= index; step += 1) result *= (order - index + step) / step;
  return result;
}

function polygonOrdinate(airfoil: Extract<LoomAirfoil, { family: "polygon" }>, x: number, upper: boolean): number {
  const hits: number[] = [];
  for (let index = 0; index < airfoil.vertices.length; index += 1) {
    const a = airfoil.vertices[index] ?? [0, 0];
    const b = airfoil.vertices[(index + 1) % airfoil.vertices.length] ?? a;
    if (x < Math.min(a[0], b[0]) || x > Math.max(a[0], b[0])) continue;
    const width = b[0] - a[0];
    hits.push(Math.abs(width) < 1e-9 ? a[1] : a[1] + ((x - a[0]) / width) * (b[1] - a[1]));
  }
  if (hits.length === 0) return 0;
  return upper ? Math.max(...hits) : Math.min(...hits);
}

function airfoilPoint(airfoil: LoomAirfoil, x: number, upper: boolean): THREE.Vector2 {
  if (airfoil.family === "cst") {
    const coefficients = upper ? airfoil.upperCoefficients : airfoil.lowerCoefficients;
    const order = coefficients.length - 1;
    const shape = coefficients.reduce((sum, coefficient, index) => (
      sum + coefficient * binomial(order, index) * x ** index * (1 - x) ** (order - index)
    ), 0);
    return new THREE.Vector2(x, Math.sqrt(x) * (1 - x) * shape);
  }
  if (airfoil.family === "polygon") {
    const thickness = airfoil.trailingEdgeThicknessRatio * x * 0.5;
    return new THREE.Vector2(x, polygonOrdinate(airfoil, x, upper) + (upper ? thickness : -thickness));
  }
  const m = airfoil.camberRatio;
  const p = airfoil.camberLocationRatio;
  const camber = m === 0 ? 0 : x < p
    ? (m / p ** 2) * (2 * p * x - x ** 2)
    : (m / (1 - p) ** 2) * (1 - 2 * p + 2 * p * x - x ** 2);
  const slope = m === 0 ? 0 : x < p
    ? (2 * m / p ** 2) * (p - x)
    : (2 * m / (1 - p) ** 2) * (p - x);
  const trailingCoefficient = airfoil.closedTrailingEdge ? 0.1036 : 0.1015;
  const thickness = 5 * airfoil.thicknessRatio * (
    0.2969 * Math.sqrt(x) - 0.126 * x - 0.3516 * x ** 2 + 0.2843 * x ** 3 - trailingCoefficient * x ** 4
  ) + airfoil.trailingEdgeThicknessRatio * x * 0.5;
  const theta = Math.atan(slope);
  return new THREE.Vector2(
    x + (upper ? -1 : 1) * thickness * Math.sin(theta),
    camber + (upper ? 1 : -1) * thickness * Math.cos(theta),
  );
}

function wingGeometry(definition: Extract<LoomPrimitiveDefinition, { primitive: "wing" }>): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const panel of definition.panels) {
    const span = panel.yTipM - panel.yRootM;
    const chordSamples = Math.min(40, Math.max(18, Math.round(panel.sectionSamples / 6)));
    const spanSamples = Math.min(33, Math.max(9, Math.round(Math.abs(span) * 4) + 1));
    const rootReferenceX = panel.xOffsetM + panel.sweepLocationRatio * panel.chordRootM;
    const base = positions.length / 3;
    const at = (spanIndex: number, upper: boolean, chordIndex: number) => (
      base + spanIndex * chordSamples * 2 + (upper ? 0 : chordSamples) + chordIndex
    );
    for (let spanIndex = 0; spanIndex < spanSamples; spanIndex += 1) {
      const v = spanIndex / (spanSamples - 1);
      const chord = THREE.MathUtils.lerp(panel.chordRootM, panel.chordTipM, v);
      const y = panel.yRootM + span * v;
      const referenceX = rootReferenceX + Math.tan(THREE.MathUtils.degToRad(panel.sweepDeg)) * span * v;
      const leadingX = referenceX - panel.sweepLocationRatio * chord;
      const z = panel.zOffsetM + Math.tan(THREE.MathUtils.degToRad(panel.dihedralDeg)) * span * v;
      const twist = THREE.MathUtils.degToRad(THREE.MathUtils.lerp(panel.twistRootDeg, panel.twistTipDeg, v));
      for (const upper of [true, false]) {
        for (let chordIndex = 0; chordIndex < chordSamples; chordIndex += 1) {
          const u = 0.5 * (1 - Math.cos(Math.PI * chordIndex / (chordSamples - 1)));
          const root = airfoilPoint(panel.airfoilRoot, u, upper);
          const tip = airfoilPoint(panel.airfoilTip, u, upper);
          const section = root.lerp(tip, v);
          const localX = (section.x - panel.twistLocationRatio) * chord;
          const localZ = section.y * chord;
          positions.push(
            leadingX + panel.twistLocationRatio * chord + localX * Math.cos(twist) + localZ * Math.sin(twist),
            y,
            z - localX * Math.sin(twist) + localZ * Math.cos(twist),
          );
        }
      }
    }
    const quad = (a: number, b: number, c: number, d: number, reverse = false) => {
      if (reverse) indices.push(a, c, b, b, c, d);
      else indices.push(a, b, c, b, d, c);
    };
    for (let spanIndex = 0; spanIndex < spanSamples - 1; spanIndex += 1) {
      for (let chordIndex = 0; chordIndex < chordSamples - 1; chordIndex += 1) {
        quad(at(spanIndex, true, chordIndex), at(spanIndex, true, chordIndex + 1), at(spanIndex + 1, true, chordIndex), at(spanIndex + 1, true, chordIndex + 1));
        quad(at(spanIndex, false, chordIndex), at(spanIndex, false, chordIndex + 1), at(spanIndex + 1, false, chordIndex), at(spanIndex + 1, false, chordIndex + 1), true);
      }
      quad(at(spanIndex, true, 0), at(spanIndex + 1, true, 0), at(spanIndex, false, 0), at(spanIndex + 1, false, 0));
      quad(at(spanIndex, true, chordSamples - 1), at(spanIndex, false, chordSamples - 1), at(spanIndex + 1, true, chordSamples - 1), at(spanIndex + 1, false, chordSamples - 1));
    }
    for (let chordIndex = 0; chordIndex < chordSamples - 1; chordIndex += 1) {
      quad(at(0, true, chordIndex), at(0, false, chordIndex), at(0, true, chordIndex + 1), at(0, false, chordIndex + 1));
      quad(at(spanSamples - 1, true, chordIndex), at(spanSamples - 1, true, chordIndex + 1), at(spanSamples - 1, false, chordIndex), at(spanSamples - 1, false, chordIndex + 1));
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function fuselageGeometry(definition: Extract<LoomPrimitiveDefinition, { primitive: "fuselage" }>): THREE.BufferGeometry {
  const ringSize = 36;
  const positions: number[] = [];
  const indices: number[] = [];
  for (const station of definition.stations) {
    for (let ring = 0; ring < ringSize; ring += 1) {
      const angle = (ring / ringSize) * Math.PI * 2;
      if (station.kind === "point") {
        positions.push(station.xM, 0, station.zCenterM);
      } else {
        const cosine = Math.cos(angle);
        const sine = Math.sin(angle);
        positions.push(
          station.xM,
          station.widthM * 0.5 * Math.sign(cosine) * Math.abs(cosine) ** (2 / station.widthExponent),
          station.zCenterM + station.heightM * 0.5 * Math.sign(sine) * Math.abs(sine) ** (2 / station.heightExponent),
        );
      }
    }
  }
  for (let station = 0; station < definition.stations.length - 1; station += 1) {
    for (let ring = 0; ring < ringSize; ring += 1) {
      const next = (ring + 1) % ringSize;
      const a = station * ringSize + ring;
      const b = station * ringSize + next;
      const c = (station + 1) * ringSize + ring;
      const d = (station + 1) * ringSize + next;
      indices.push(a, c, b, b, c, d);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function primitiveGeometry(definition: LoomPrimitiveDefinition): THREE.BufferGeometry {
  if (definition.primitive === "sphere") return new THREE.SphereGeometry(definition.radiusM, 32, 18);
  if (definition.primitive === "box") return new THREE.BoxGeometry(...definition.dimensionsM);
  if (definition.primitive === "ellipsoid") {
    const geometry = new THREE.SphereGeometry(1, 32, 18);
    geometry.scale(...definition.radiiM);
    return geometry;
  }
  if (definition.primitive === "pod") {
    const geometry = new THREE.SphereGeometry(1, 32, 18);
    const radius = definition.lengthM / (2 * definition.finenessRatio);
    geometry.scale(definition.lengthM * 0.5, radius, radius);
    geometry.translate(definition.noseXM + definition.lengthM * 0.5, 0, 0);
    return geometry;
  }
  if (definition.primitive === "wing") return wingGeometry(definition);
  if (definition.primitive === "fuselage") return fuselageGeometry(definition);
  if (definition.primitive === "extrusion") {
    const points = profilePoints(definition.profile);
    const shape = new THREE.Shape(points);
    const geometry = new THREE.ExtrudeGeometry(shape, { bevelEnabled: false, depth: definition.halfLengthM * 2 });
    geometry.translate(0, 0, -definition.halfLengthM);
    if (definition.plane === "xz") geometry.rotateX(Math.PI / 2);
    if (definition.plane === "yz") geometry.rotateY(Math.PI / 2);
    return geometry;
  }
  const points = profilePoints(definition.profile).map((point) => new THREE.Vector2(
    Math.max(0.001, definition.radialOffsetM + point.x),
    point.y,
  ));
  const geometry = new THREE.LatheGeometry(points, 48);
  geometry.rotateZ(Math.PI / 2);
  if (definition.axis === "y") geometry.rotateZ(-Math.PI / 2);
  if (definition.axis === "z") geometry.rotateY(Math.PI / 2);
  return geometry;
}

const AUTHORED_SURFACE_COLORS: Partial<Record<LoomPrimitiveDefinition["primitive"], string>> = {
  fuselage: "#5f7288",
  wing: "#4e8290",
  pod: "#6f7884",
  ellipsoid: "#6f7884",
  sphere: "#75828d",
  box: "#657a84",
  extrusion: "#667d80",
  revolution: "#6d7688",
};

function authoredSurfaceColor(primitive: unknown, fallback: string): string {
  return typeof primitive === "string"
    ? AUTHORED_SURFACE_COLORS[primitive as LoomPrimitiveDefinition["primitive"]] ?? fallback
    : fallback;
}

function reverseTriangleWinding(geometry: THREE.BufferGeometry): void {
  const index = geometry.index;
  if (index) {
    for (let offset = 0; offset < index.count; offset += 3) {
      const second = index.getX(offset + 1);
      index.setX(offset + 1, index.getX(offset + 2));
      index.setX(offset + 2, second);
    }
    index.needsUpdate = true;
    return;
  }
  Object.values(geometry.attributes).forEach((attribute) => {
    for (let offset = 0; offset < attribute.count; offset += 3) {
      for (let component = 0; component < attribute.itemSize; component += 1) {
        const second = attribute.getComponent(offset + 1, component);
        attribute.setComponent(offset + 1, component, attribute.getComponent(offset + 2, component));
        attribute.setComponent(offset + 2, component, second);
      }
    }
    attribute.needsUpdate = true;
  });
}

function mirrorObject(object: THREE.Object3D, axis: LoomAxis, planeOffsetM: number): THREE.Group {
  object.updateMatrixWorld(true);
  const reflection = new THREE.Matrix4().makeScale(axis === "x" ? -1 : 1, axis === "y" ? -1 : 1, axis === "z" ? -1 : 1);
  reflection.setPosition(axis === "x" ? 2 * planeOffsetM : 0, axis === "y" ? 2 * planeOffsetM : 0, axis === "z" ? 2 * planeOffsetM : 0);
  const result = new THREE.Group();
  result.name = object.name;
  result.userData = { ...object.userData };
  object.traverse((child) => {
    const mesh = child as THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>;
    if (!mesh.isMesh) return;
    const matrix = reflection.clone().multiply(mesh.matrixWorld);
    const geometry = mesh.geometry.clone();
    geometry.applyMatrix4(matrix);
    if (matrix.determinant() < 0) reverseTriangleWinding(geometry);
    geometry.computeVertexNormals();
    const material = Array.isArray(mesh.material) ? mesh.material.map((item) => item.clone()) : mesh.material.clone();
    const reflected = new THREE.Mesh(geometry, material);
    reflected.name = mesh.name;
    reflected.renderOrder = mesh.renderOrder;
    reflected.userData = { ...mesh.userData };
    result.add(reflected);
  });
  return result;
}

function applyTransform(object: THREE.Object3D, node: Extract<LoomSceneNode, { type: "transform" }>): THREE.Object3D {
  if (node.operation.transform === "mirror-y") return mirrorObject(object, "y", 0);
  if (node.operation.transform === "mirror") return mirrorObject(object, node.operation.axis, node.operation.planeOffsetM);
  const matrix = new THREE.Matrix4();
  if (node.operation.transform === "translate") matrix.makeTranslation(...node.operation.offsetM);
  else if (node.operation.transform === "uniform-scale") matrix.makeScale(
    node.operation.factor,
    node.operation.factor,
    node.operation.factor,
  );
  else {
    const axis = new THREE.Vector3(
      node.operation.axis === "x" ? 1 : 0,
      node.operation.axis === "y" ? 1 : 0,
      node.operation.axis === "z" ? 1 : 0,
    );
    const origin = new THREE.Vector3().fromArray(node.operation.originM);
    const angle = THREE.MathUtils.degToRad(node.operation.angleDeg);
    matrix.makeTranslation(origin.x, origin.y, origin.z)
      .multiply(new THREE.Matrix4().makeRotationAxis(axis, angle))
      .multiply(new THREE.Matrix4().makeTranslation(-origin.x, -origin.y, -origin.z));
  }
  const result = new THREE.Group();
  result.name = `Definition transform: ${node.label}`;
  result.userData = { ...object.userData };
  result.matrixAutoUpdate = false;
  result.matrix.copy(matrix);
  result.add(object);
  return result;
}

export function buildLoomAuthoringPreview(
  document: LoomAuthoringDocument,
  selectedNodeId: string | null,
  color = "#2368a2",
): THREE.Group {
  const group = new THREE.Group();
  group.name = "Loom authoring definition preview";
  if (!selectedNodeId) return group;
  const byId = new Map(document.nodes.map((node) => [node.id, node]));
  const build = (nodeId: string, instancePath: readonly string[] = []): THREE.Object3D | null => {
    const node = byId.get(nodeId);
    if (!node || node.type === "import") return null;
    const nextPath = [...instancePath, node.id];
    if (node.type === "primitive") {
      const material = new THREE.MeshPhysicalMaterial({
        clearcoat: 0.22,
        clearcoatRoughness: 0.58,
        color,
        depthTest: true,
        envMapIntensity: 0.7,
        flatShading: false,
        metalness: 0.035,
        opacity: 0.78,
        reflectivity: 0.34,
        roughness: 0.5,
        transparent: true,
        wireframe: true,
      });
      material.userData = { loomFinish: "satin-enamel", loomLightReactive: true };
      const mesh = new THREE.Mesh(primitiveGeometry(node.definition), material);
      mesh.name = `Definition preview: ${node.label}`;
      mesh.userData.loomEditableNodeId = node.id;
      mesh.userData.loomNodeId = node.id;
      mesh.userData.loomInstancePath = nextPath;
      mesh.userData.loomPrimitiveType = node.definition.primitive;
      return mesh;
    }
    if (node.type === "transform") {
      let object = build(node.inputNodeId, nextPath);
      if (object) object = applyTransform(object, node);
      if (object) {
        object.traverse((child) => {
          const mesh = child as THREE.Mesh;
          if (!mesh.isMesh) return;
          mesh.userData.loomEditableNodeId = node.id;
          mesh.userData.loomNodeId = node.id;
          if (node.operation.transform !== "mirror") return;
          const operation = node.operation;
          mesh.userData.loomMirrorSourceNodeId = node.inputNodeId;
          mesh.userData.loomMirrorAxis = operation.axis;
          mesh.userData.loomMirrorPlaneOffsetM = operation.planeOffsetM;
          mesh.userData.loomAeroGate = loomMirrorAeroGate(operation);
        });
      }
      return object;
    }
    const booleanGroup = new THREE.Group();
    booleanGroup.name = `Unextracted ${node.operation} operands`;
    for (const inputId of node.inputNodeIds) {
      const object = build(inputId, nextPath);
      if (object) booleanGroup.add(object);
    }
    return booleanGroup;
  };
  const preview = build(selectedNodeId);
  if (preview) group.add(preview);
  return group;
}

export function buildLoomAuthoringSelectionScene(
  document: LoomAuthoringDocument,
  selectedNodeId: string | null,
  selectedColor = "#2368a2",
  mode: "edited-preview" | "pick-only" = "edited-preview",
  surfaceColor = "#317b58",
): THREE.Group {
  const group = new THREE.Group();
  group.name = "Loom selectable authoring operands";
  const referenced = new Set<string>();
  for (const node of document.nodes) {
    if (node.type === "transform" && node.operation.transform !== "mirror") referenced.add(node.inputNodeId);
    if (node.type === "boolean") node.inputNodeIds.forEach((id) => referenced.add(id));
  }
  const visibleRoots = [
    ...document.rootNodeIds,
    ...document.nodes
      .filter((node) => !referenced.has(node.id) && !document.rootNodeIds.includes(node.id))
      .map((node) => node.id),
  ];
  const selectedNode = document.nodes.find((node) => node.id === selectedNodeId);
  for (const nodeId of visibleRoots) {
    const preview = buildLoomAuthoringPreview(document, nodeId, selectedColor);
    preview.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      const path = Array.isArray(mesh.userData.loomInstancePath)
        ? mesh.userData.loomInstancePath as string[]
        : [];
      const selected = selectedNodeId !== null && (
        mesh.userData.loomNodeId === selectedNodeId
        || (selectedNode?.type === "boolean" && path.includes(selectedNodeId))
        || (selectedNode?.type === "transform" && selectedNode.operation.transform !== "mirror" && path.includes(selectedNodeId))
        || (selectedNode?.type === "primitive" && path.includes(selectedNodeId)
          && mesh.userData.loomMirrorSourceNodeId !== selectedNodeId)
      );
      const material = mesh.material as THREE.MeshPhysicalMaterial;
      mesh.userData.loomActiveSelection = selected;
      material.color.set(selected
        ? selectedColor
        : authoredSurfaceColor(mesh.userData.loomPrimitiveType, surfaceColor));
      material.depthWrite = mode === "edited-preview";
      material.emissive.set(selected ? selectedColor : "#000000");
      material.emissiveIntensity = selected ? 0.12 : 0;
      material.opacity = mode === "edited-preview" ? 1 : 0;
      material.transparent = mode === "pick-only";
      material.wireframe = false;
      mesh.renderOrder = selected ? 12 : 11;
    });
    group.add(preview);
  }
  addLoomSelectionOutlines(group, selectedColor);
  return group;
}
